import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { diagnosticAPI } from '../services/api';
import Logo from '../components/Logo';
import './DiagnosticOnboarding.css';

interface DiagnosticInfo {
  available: boolean;
  assessment_id: string;
  question_count: number;
  taken: boolean;
  score: number | null;
}

const SUBJECT_META: Record<string, { icon: string; color: string }> = {
  Biology: { icon: '🧬', color: '#4CAF50' },
  Chemistry: { icon: '⚗️', color: '#2196F3' },
  Physics: { icon: '⚛️', color: '#FF9800' },
  Mathematics: { icon: '📐', color: '#9C27B0' },
  English: { icon: '📚', color: '#3F51B5' },
  History: { icon: '📜', color: '#795548' },
  Geography: { icon: '🌍', color: '#009688' },
  Economics: { icon: '💰', color: '#FF5722' },
};

const DiagnosticOnboarding: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();
  const [diagnosticStatus, setDiagnosticStatus] = useState<Record<string, DiagnosticInfo>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDiagnosticStatus();
  }, []);

  const fetchDiagnosticStatus = async () => {
    try {
      setLoading(true);
      const data = await diagnosticAPI.getStatus();
      setDiagnosticStatus(data.diagnostic_status || {});
    } catch (err) {
      console.error('Error fetching diagnostic status:', err);
    } finally {
      setLoading(false);
    }
  };

  const [starting, setStarting] = useState<string | null>(null);

  const handleSubjectClick = async (subject: string) => {
    const info = diagnosticStatus[subject];
    if (!info?.available || starting) return;

    if (info.taken) {
      // Generate fresh questions for retake
      try {
        setStarting(subject);
        const result = await diagnosticAPI.start(subject);
        navigate('/take-assessment', {
          state: {
            assessment: {
              id: result.assessment_id,
              title: `Knowledge Check - ${subject}`,
              question_count: result.question_count,
            },
            isDiagnostic: true,
            isInitial: false,
          }
        });
      } catch (err) {
        console.error('Failed to start diagnostic:', err);
        setStarting(null);
      }
    } else {
      navigate('/take-assessment', {
        state: {
          assessment: {
            id: info.assessment_id,
            title: `Knowledge Check - ${subject}`,
            question_count: info.question_count,
          },
          isDiagnostic: true,
          isInitial: true,
        }
      });
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  // Separate subjects into completed and available
  const allSubjects = Object.entries(diagnosticStatus).filter(([_, info]) => info.available);
  const completedSubjects = allSubjects.filter(([_, info]) => info.taken);
  const availableSubjects = allSubjects.filter(([_, info]) => !info.taken);
  const allDone = allSubjects.length > 0 && availableSubjects.length === 0;

  // Dynamic header text
  const getHeaderText = () => {
    if (allDone) return "You've checked all your subjects!";
    if (completedSubjects.length > 0) return 'Check another subject';
    return 'Which subject do you want to focus on first?';
  };

  const getSubheaderText = () => {
    if (allDone) return 'Great job! Head to your Dashboard to start studying.';
    if (completedSubjects.length > 0) return 'Keep going — the more subjects you check, the better your study plan.';
    return 'Take a quick knowledge check to find out what you know and what needs work. It takes about 15 minutes — skip what you haven\'t studied yet.';
  };

  return (
    <div className="diagnostic-onboarding">
      <div className="onboarding-content">
          <div className="onboarding-brand">
            <Logo height={40} />
          </div>
          <div className="onboarding-header">
            <h1>{getHeaderText()}</h1>
            <p>{getSubheaderText()}</p>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading subjects...</p>
            </div>
          ) : allSubjects.length === 0 ? (
            <div className="no-diagnostics">
              <p>Knowledge checks are being prepared. Head to your Dashboard to start learning!</p>
              <button onClick={handleSkip} className="skip-button primary">
                Go to Dashboard
              </button>
            </div>
          ) : allDone ? (
            <div className="all-done-state">
              <div className="subject-grid">
                {completedSubjects.map(([subject, info]) => {
                  const meta = SUBJECT_META[subject] || { icon: '📚', color: '#4CAF50' };
                  return (
                    <button
                      key={subject}
                      className="subject-option completed"
                      style={{ '--subject-color': meta.color } as React.CSSProperties}
                      onClick={() => handleSubjectClick(subject)}
                      disabled={starting === subject}
                    >
                      <span className="subject-emoji">{meta.icon}</span>
                      <span className="subject-name">{subject}</span>
                      <span className="completed-score">
                        {starting === subject ? '...' : `${Math.round(info.score || 0)}% · Retake`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button onClick={handleSkip} className="skip-button primary">
                Go to Dashboard
              </button>
            </div>
          ) : (
            <>
              <div className="subject-grid">
                {/* Available subjects first */}
                {availableSubjects.map(([subject]) => {
                  const meta = SUBJECT_META[subject] || { icon: '📚', color: '#4CAF50' };
                  const info = diagnosticStatus[subject];
                  return (
                    <button
                      key={subject}
                      className="subject-option"
                      style={{ '--subject-color': meta.color } as React.CSSProperties}
                      onClick={() => handleSubjectClick(subject)}
                    >
                      <span className="subject-emoji">{meta.icon}</span>
                      <span className="subject-name">{subject}</span>
                      <span className="question-count">{info.question_count} questions</span>
                    </button>
                  );
                })}

                {/* Completed subjects — clickable for retake */}
                {completedSubjects.map(([subject, info]) => {
                  const meta = SUBJECT_META[subject] || { icon: '📚', color: '#4CAF50' };
                  return (
                    <button
                      key={subject}
                      className="subject-option completed"
                      style={{ '--subject-color': meta.color } as React.CSSProperties}
                      onClick={() => handleSubjectClick(subject)}
                      disabled={starting === subject}
                    >
                      <span className="subject-emoji">{meta.icon}</span>
                      <span className="subject-name">{subject}</span>
                      <span className="completed-score">
                        {starting === subject ? '...' : `${Math.round(info.score || 0)}% · Retake`}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button onClick={handleSkip} className="skip-link">
                Skip for now — go to Dashboard →
              </button>
            </>
          )}
        </div>
    </div>
  );
};

export default DiagnosticOnboarding;
